import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, Download, Eye, ArrowLeft, Stethoscope, Heart, Shield, BookOpen } from "lucide-react";
import EnhancedExportButton from "@/components/EnhancedExportButton";
const AssessmentComplete = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    answers,
    result,
    enhancedData,
    aiScore,
    confidence
  } = location.state || {};

  // Redirect if no assessment data
  if (!aiScore) {
    return <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-4">No Assessment Data Found</h2>
          <p className="text-muted-foreground mb-4">
            It looks like you haven't completed an assessment yet.
          </p>
          <Button onClick={() => navigate('/')}>
            Take Assessment
          </Button>
        </Card>
      </div>;
  }
  const riskLevel = aiScore < 20 ? 'Very Low Risk' : aiScore < 40 ? 'Minimal Concern' : aiScore < 60 ? 'Moderate Risk' : aiScore < 80 ? 'Elevated Risk' : 'High Priority';
  return <div className="min-h-screen bg-background">
      {/* Header with same buttons as next steps page */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100">
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              <button onClick={() => navigate("/")} className="flex items-center space-x-2 hover:opacity-80 transition-opacity min-w-0">
                <div className="bg-gray-900 p-1.5 rounded-lg flex-shrink-0">
                  <Stethoscope className="h-4 w-4 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 truncate">AmIOK</h1>
              </button>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <EnhancedExportButton answers={answers} aiScore={aiScore} riskLevel={riskLevel} confidence={confidence} enhancedData={enhancedData} potentialDiagnoses={enhancedData?.diagnosticAnalysis?.potentialDiagnoses?.map(d => d.condition) || []} recommendations={enhancedData?.recommendations || []} compact={true} />
              <Button variant="ghost" onClick={() => navigate('/results/assessment', {
              state: {
                answers,
                result,
                enhancedData,
                aiScore,
                confidence
              }
            })} className="text-gray-600 hover:text-gray-700 text-sm px-3 py-2 flex items-center gap-2" size="sm">
                <Eye className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Results</span>
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="text-blue-600 hover:text-blue-700 text-sm px-3 py-2" size="sm">
                <ArrowLeft className="mr-1 h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Completion Status Card */}
        <Card className="p-8 text-center bg-gradient-to-br from-green-50 to-blue-50/50 border-green-200">
          <div className="space-y-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <div className="space-y-3">
              <h1 className="text-3xl font-bold text-gray-900">
                Assessment Complete!
              </h1>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">Congratulations! You have successfully completed the AmIOK health assessment!</p>
            </div>
          </div>
        </Card>

        {/* Key Information Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-6 text-center border-blue-200 bg-blue-50/50">
            <div className="space-y-3">
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Heart className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Follow Recommendations</h3>
              <p className="text-sm text-gray-600">
                Carefully follow the personalized recommendations and action plans provided in your assessment results.
              </p>
            </div>
          </Card>

          <Card className="p-6 text-center border-purple-200 bg-purple-50/50">
            <div className="space-y-3">
              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Use Healthcare Contacts</h3>
              <p className="text-sm text-gray-600">
                Make use of the healthcare contacts and specialist recommendations provided for your specific needs.
              </p>
            </div>
          </Card>

          <Card className="p-6 text-center border-orange-200 bg-orange-50/50">
            <div className="space-y-3">
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Access Resources</h3>
              <p className="text-sm text-gray-600">
                Utilize all the educational resources and support materials provided to maintain your health.
              </p>
            </div>
          </Card>
        </div>

        {/* Important Reminders */}
        <Card className="p-6 bg-yellow-50/50 border-yellow-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Important Reminders</h2>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start space-x-3">
              <div className="bg-yellow-100 text-yellow-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                1
              </div>
              <p className="text-sm">
                Your AmIOK assessment provides evidence-based guidance, but should not replace professional medical advice when needed.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-yellow-100 text-yellow-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                2
              </div>
              <p className="text-sm">
                Keep your assessment results and downloaded reports for reference and to share with healthcare providers if needed.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-yellow-100 text-yellow-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                3
              </div>
              <p className="text-sm">
                Monitor your symptoms as recommended and seek immediate medical attention if your condition worsens or if you experience any emergency symptoms.
              </p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => navigate('/results/next-steps', {
          state: {
            answers,
            result,
            enhancedData,
            aiScore,
            confidence
          }
        })} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Next Steps
          </Button>
          
          <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2">
            <Stethoscope className="h-4 w-4" />
            Take New Assessment
          </Button>
        </div>

        {/* Footer Note */}
        <div className="text-center pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Thank you for using AmIOK. Your health and wellbeing are our priority.
          </p>
        </div>
      </main>
    </div>;
};
export default AssessmentComplete;